import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getOrganization,
  getOrganizationMembers,
  updateOrganizationSettings,
} from "../../utils/Api";
import LoadingPage from "../../components/LoadingPage";
import { ArrowLeft, Check, X, Plus, Phone, Mail, Info } from "lucide-react";
import LoadingIcons from "react-loading-icons";

interface Organization {
  id: string;
  name: string;
  allow_mobile_upload: boolean;
  allow_member_download: boolean;
  hide_member_emails: boolean;
  contact_phones: string[];
  contact_emails: string[];
  created_at: string;
}

interface AdminMember {
  email: string;
  name: string | null;
  created_at: string;
}

const OrganizationSettings: React.FC = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const navigate = useNavigate();

  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState("");
  const [savingName, setSavingName] = useState(false);

  const [adminMembers, setAdminMembers] = useState<AdminMember[]>([]);
  const [firstAdminEmail, setFirstAdminEmail] = useState<string | null>(null);
  const [newPhone, setNewPhone] = useState("");
  const [selectedEmail, setSelectedEmail] = useState("");
  const [savingContact, setSavingContact] = useState(false);

  useEffect(() => {
    if (!organizationId) return;

    let cancelled = false;

    const loadOrganization = async () => {
      try {
        setLoading(true);
        const response = await getOrganization(organizationId);
        const userRole = response.data.user_role;

        if (userRole.toLowerCase() !== "admin") {
          navigate(`/home/organization/${organizationId}`, { replace: true });
          return;
        }

        if (!cancelled) {
          setOrganization(response.data.organization);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err.response?.data?.detail || "Failed to load organisation");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    const loadAdminMembers = async () => {
      try {
        const response = await getOrganizationMembers(organizationId);
        if (!cancelled) {
          const admins: AdminMember[] = response.data.members
            .filter(
              (m: any) =>
                m.role.toLowerCase() === "admin" && m.status === "active",
            )
            .map((m: any) => ({
              email: m.email,
              name: m.name,
              created_at: m.created_at,
            }))
            .sort((a: AdminMember, b: AdminMember) =>
              a.created_at.localeCompare(b.created_at),
            );
          setAdminMembers(admins);
          const firstAdmin = admins[0];
          if (firstAdmin) {
            setFirstAdminEmail(firstAdmin.email);
          }
        }
      } catch {
        // silently fail — admin emails are not critical
      }
    };

    loadOrganization();
    loadAdminMembers();

    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const handleStartEditingName = () => {
    if (!organization) return;
    setNameValue(organization.name);
    setEditingName(true);
  };

  const handleCancelEditingName = () => {
    setEditingName(false);
    setNameValue("");
  };

  const handleSaveName = async () => {
    if (!organization || savingName) return;
    const trimmed = nameValue.trim();
    if (!trimmed || trimmed === organization.name) {
      setEditingName(false);
      return;
    }

    setSavingName(true);
    try {
      await updateOrganizationSettings(organizationId!, { name: trimmed });
      setOrganization({ ...organization, name: trimmed });
      setEditingName(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to update name");
    } finally {
      setSavingName(false);
    }
  };

  const handleToggleMobileUpload = async () => {
    if (!organization || saving) return;

    const newValue = !organization.allow_mobile_upload;
    setOrganization({ ...organization, allow_mobile_upload: newValue });
    setSaving(true);

    try {
      await updateOrganizationSettings(organizationId!, {
        allow_mobile_upload: newValue,
      });
    } catch (err: any) {
      setOrganization({ ...organization, allow_mobile_upload: !newValue });
      setError(err.response?.data?.detail || "Failed to update settings");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleHideMemberEmails = async () => {
    if (!organization || saving) return;

    const newValue = !organization.hide_member_emails;
    setOrganization({ ...organization, hide_member_emails: newValue });
    setSaving(true);

    try {
      await updateOrganizationSettings(organizationId!, {
        hide_member_emails: newValue,
      });
    } catch (err: any) {
      setOrganization({ ...organization, hide_member_emails: !newValue });
      setError(err.response?.data?.detail || "Failed to update settings");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleMemberDownload = async () => {
    if (!organization || saving) return;

    const newValue = !organization.allow_member_download;
    setOrganization({ ...organization, allow_member_download: newValue });
    setSaving(true);

    try {
      await updateOrganizationSettings(organizationId!, {
        allow_member_download: newValue,
      });
    } catch (err: any) {
      setOrganization({ ...organization, allow_member_download: !newValue });
      setError(err.response?.data?.detail || "Failed to update settings");
    } finally {
      setSaving(false);
    }
  };

  const handleAddPhone = async () => {
    if (!organization || savingContact) return;
    const trimmed = newPhone.trim();
    if (!trimmed) return;

    const updated = [...organization.contact_phones, trimmed];
    setOrganization({ ...organization, contact_phones: updated });
    setNewPhone("");
    setSavingContact(true);

    try {
      await updateOrganizationSettings(organizationId!, {
        contact_phones: updated,
      });
    } catch (err: any) {
      setOrganization({
        ...organization,
        contact_phones: organization.contact_phones,
      });
      setError(err.response?.data?.detail || "Failed to add phone number");
    } finally {
      setSavingContact(false);
    }
  };

  const handleRemovePhone = async (index: number) => {
    if (!organization || savingContact) return;
    const previous = organization.contact_phones;
    const updated = previous.filter((_, i) => i !== index);
    setOrganization({ ...organization, contact_phones: updated });
    setSavingContact(true);

    try {
      await updateOrganizationSettings(organizationId!, {
        contact_phones: updated,
      });
    } catch (err: any) {
      setOrganization({ ...organization, contact_phones: previous });
      setError(err.response?.data?.detail || "Failed to remove phone number");
    } finally {
      setSavingContact(false);
    }
  };

  const handleAddEmail = async () => {
    if (!organization || savingContact || !selectedEmail) return;

    const updated = [...organization.contact_emails, selectedEmail];
    setOrganization({ ...organization, contact_emails: updated });
    setSelectedEmail("");
    setSavingContact(true);

    try {
      await updateOrganizationSettings(organizationId!, {
        contact_emails: updated,
      });
    } catch (err: any) {
      setOrganization({
        ...organization,
        contact_emails: organization.contact_emails,
      });
      setError(err.response?.data?.detail || "Failed to add email");
    } finally {
      setSavingContact(false);
    }
  };

  const handleRemoveEmail = async (index: number) => {
    if (!organization || savingContact) return;
    if (organization.contact_emails[index] === firstAdminEmail) return;
    const previous = organization.contact_emails;
    const updated = previous.filter((_, i) => i !== index);
    setOrganization({ ...organization, contact_emails: updated });
    setSavingContact(true);

    try {
      await updateOrganizationSettings(organizationId!, {
        contact_emails: updated,
      });
    } catch (err: any) {
      setOrganization({ ...organization, contact_emails: previous });
      setError(err.response?.data?.detail || "Failed to remove email");
    } finally {
      setSavingContact(false);
    }
  };

  const availableAdminEmails = adminMembers.filter(
    (admin: AdminMember) => !organization?.contact_emails.includes(admin.email),
  );

  if (loading) {
    return (
      <LoadingPage
        fullScreen={false}
        variant="light"
        message="Loading organisation settings"
      />
    );
  }

  if (error && !organization) {
    return (
      <div className="bg-gray-100 min-h-full">
        <div className="w-full mx-auto py-8 px-4 sm:py-12 sm:px-10">
          <div
            className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded"
            role="alert"
          >
            {error}
          </div>
        </div>
      </div>
    );
  }

  if (!organization) return null;

  return (
    <div className="bg-gray-100 h-full flex flex-col overflow-hidden">
      <div className="w-full mx-auto pt-6 px-4 sm:pt-12 sm:px-10 flex-shrink-0">
        <div className="mb-6">
          <button
            onClick={() => navigate(`/home/organization/${organizationId}`)}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors mb-4"
          >
            <ArrowLeft size={16} />
            Back to organisation
          </button>
          <h3 className="text-2xl font-semibold">Organisation settings</h3>
          <p className="text-gray-500">{organization.name}</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 sm:px-10 pt-4 sm:pt-6 pb-8 sm:pb-12">
        {error && (
          <div
            className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4"
            role="alert"
          >
            {error}
          </div>
        )}

        <h6 className="text-base font-semibold">General</h6>
        <div className="border border-gray-300 rounded p-4 mt-3 bg-white">
          <div className="flex items-center justify-between max-[600px]:flex-col max-[600px]:items-start max-[600px]:gap-2">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">
                Organisation name
              </p>
            </div>
            {editingName ? (
              <div className="flex items-center gap-2 max-[600px]:w-full">
                <input
                  type="text"
                  value={nameValue}
                  onChange={(e) => setNameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveName();
                    if (e.key === "Escape") handleCancelEditingName();
                  }}
                  disabled={savingName}
                  className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 max-[600px]:flex-1"
                  autoFocus
                />
                {savingName ? (
                  <LoadingIcons.TailSpin
                    stroke="#6b7280"
                    width="20"
                    height="20"
                  />
                ) : (
                  <>
                    <button
                      onClick={handleSaveName}
                      className="p-1 text-green-600 hover:text-green-700 transition-colors"
                      aria-label="Save name"
                    >
                      <Check size={18} />
                    </button>
                    <button
                      onClick={handleCancelEditingName}
                      className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                      aria-label="Cancel editing"
                    >
                      <X size={18} />
                    </button>
                  </>
                )}
              </div>
            ) : (
              <button
                onClick={handleStartEditingName}
                className="text-sm text-gray-700 hover:text-gray-900 transition-colors cursor-pointer"
              >
                {organization.name}
              </button>
            )}
          </div>
        </div>

        <h6 className="text-base font-semibold mt-6">Privacy</h6>
        <div className="border border-gray-300 rounded p-4 mt-3 bg-white">
          <div className="flex items-start justify-between gap-4 max-[600px]:flex-col max-[600px]:gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 flex items-center gap-1">
                Hide member email addresses
                <span className="relative group">
                  <Info className="h-4 w-4 text-gray-400 cursor-help flex-shrink-0" />
                  <span className="absolute bottom-full left-0 sm:left-1/2 sm:-translate-x-1/2 mb-1 hidden group-hover:block w-48 text-xs text-white bg-gray-800 rounded px-2 py-1 sm:text-center z-10">
                    Admins will still be able to see member's emails
                  </span>
                </span>
              </p>
              <p className="text-sm text-gray-500">
                Hide members' email addresses from other members.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={organization.hide_member_emails}
              onClick={handleToggleHideMemberEmails}
              disabled={saving}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                organization.hide_member_emails ? "bg-blue-600" : "bg-gray-200"
              } ${saving ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  organization.hide_member_emails
                    ? "translate-x-5"
                    : "translate-x-0"
                }`}
              />
            </button>
          </div>
        </div>

        <h6 className="text-base font-semibold mt-6">Documents management</h6>
        <div className="border border-gray-300 rounded p-4 mt-3 bg-white">
          <div className="flex items-start justify-between gap-4 max-[600px]:flex-col max-[600px]:gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">
                Allow mobile uploads
              </p>
              <p className="text-sm text-gray-500">
                Allow members to upload documents from mobile devices
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={organization.allow_mobile_upload}
              onClick={handleToggleMobileUpload}
              disabled={saving}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                organization.allow_mobile_upload ? "bg-blue-600" : "bg-gray-200"
              } ${saving ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  organization.allow_mobile_upload
                    ? "translate-x-5"
                    : "translate-x-0"
                }`}
              />
            </button>
          </div>
        </div>

        <div className="border border-gray-300 rounded p-4 mt-3 bg-white">
          <div className="flex items-start justify-between gap-4 max-[600px]:flex-col max-[600px]:gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 flex items-center gap-1">
                Allow member downloads
                <span className="relative group">
                  <Info className="h-4 w-4 text-gray-400 cursor-help flex-shrink-0" />
                  <span className="absolute bottom-full left-0 sm:left-1/2 sm:-translate-x-1/2 mb-1 hidden group-hover:block w-48 text-xs text-white bg-gray-800 rounded px-2 py-1 sm:text-center z-10">
                    Admins will still be able to download as before.
                  </span>
                </span>
              </p>

              <p className="text-sm text-gray-500">
                Allow members to download documents, pages, and JSON data.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={organization.allow_member_download}
              onClick={handleToggleMemberDownload}
              disabled={saving}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                organization.allow_member_download
                  ? "bg-blue-600"
                  : "bg-gray-200"
              } ${saving ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  organization.allow_member_download
                    ? "translate-x-5"
                    : "translate-x-0"
                }`}
              />
            </button>
          </div>
        </div>

        <h6 className="text-base font-semibold mt-6">Contact information</h6>
        <p className="text-sm text-gray-500 mt-1">
          Contact details visible to all organisation members
        </p>
        <div className="border border-gray-300 rounded p-4 mt-3 bg-white space-y-5">
          {/* Phone numbers */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Phone size={16} className="text-gray-500" />
              <p className="text-sm font-medium text-gray-900">Phone numbers</p>
            </div>

            {organization.contact_phones.length > 0 && (
              <div className="space-y-2 mb-3">
                {organization.contact_phones.map((phone, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-gray-50 rounded px-3 py-2"
                  >
                    <span className="text-sm text-gray-700 truncate min-w-0">
                      {phone}
                    </span>
                    <button
                      onClick={() => handleRemovePhone(index)}
                      disabled={savingContact}
                      className="text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                      aria-label="Remove phone number"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center gap-2 max-[600px]:flex-col max-[600px]:items-stretch">
              <input
                type="tel"
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAddPhone();
                }}
                placeholder="Add phone number"
                disabled={savingContact}
                className="flex-1 border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 min-w-0"
              />
              <button
                onClick={handleAddPhone}
                disabled={savingContact || !newPhone.trim()}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-300 rounded hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus size={14} />
                Add
              </button>
            </div>
          </div>

          <hr className="border-gray-200" />

          {/* Email addresses */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Mail size={16} className="text-gray-500" />
              <p className="text-sm font-medium text-gray-900">
                Email addresses
              </p>
            </div>

            {organization.contact_emails.length > 0 && (
              <div className="space-y-2 mb-3">
                {organization.contact_emails.map((email, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-gray-50 rounded px-3 py-2"
                  >
                    <span className="text-sm text-gray-700 truncate min-w-0">
                      {email}
                    </span>
                    {email !== firstAdminEmail ? (
                      <button
                        onClick={() => handleRemoveEmail(index)}
                        disabled={savingContact}
                        className="text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                        aria-label="Remove email"
                      >
                        <X size={16} />
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400">Default</span>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center gap-2 max-[600px]:flex-col max-[600px]:items-stretch">
              <select
                value={selectedEmail}
                onChange={(e) => setSelectedEmail(e.target.value)}
                disabled={savingContact || availableAdminEmails.length === 0}
                className="flex-1 border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 bg-white min-w-0"
              >
                <option value="">
                  {availableAdminEmails.length === 0
                    ? "No admin emails available"
                    : "Select admin email"}
                </option>
                {availableAdminEmails.map((admin: AdminMember) => (
                  <option key={admin.email} value={admin.email}>
                    {admin.name && admin.name !== admin.email
                      ? `${admin.name} (${admin.email})`
                      : admin.email}
                  </option>
                ))}
              </select>
              <button
                onClick={handleAddEmail}
                disabled={savingContact || !selectedEmail}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-300 rounded hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus size={14} />
                Add
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationSettings;
